/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: ~880 lines
 * - Adding: ~20 lines (inline comments)
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    USER_DELETE_FEATURE, STATUS_CHANGE_CONFIRMATION, ADMIN_PASSWORD_RESET, AUDIT_LOGGING_IMPLEMENTATION
 * Date:         2025-01-13, 2025-01-14, 2025-01-15
 * Modified By:  Cursor
 *
 * Description:
 * Added delete user functionality with confirmation dialog, proper error handling,
 * and optimistic UI updates. Includes prevention of self-deletion.
 * Added status change confirmation dialog to prevent accidental status changes
 * and inform admins of the impact when changing user status.
 * Added admin password reset functionality allowing admins to trigger password reset emails for users.
 * Added audit log tab to display activity history for user accounts.
 *
 * Related Screens / Modules:
 * - client/src/features/admin/users/UserEditModal.tsx
 * - client/src/features/admin/users/ConfirmDialog.tsx
 * - client/src/services/adminUsersService.ts
 * - client/src/features/admin/users/components/UserAuditLog.tsx
 */
import React, { useState, useEffect, useCallback } from "react";
import { Modal, Form, Button, Row, Col, Badge, Spinner, Alert, Nav } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faEnvelope,
  faShieldAlt,
  faToggleOn,
  faTrash,
  faKey,
  faHistory,
} from "@fortawesome/free-solid-svg-icons";
import { AdminUser, AdminUserUpdateData, UserStatus } from "../../../interfaces/adminUsers";
import {
  getUser,
  updateUser,
  deleteUser,
  resetUserPassword,
} from "../../../services/adminUsersService";
import { formatDateTime } from "../../../utils/dateFormat";
import { useUserCountry } from "../../../hooks/useUserCountry";
// [2026-07-08] cookie-auth-fix: use cookie-based checkAuthStatus instead of deprecated getUserInfoFromToken (localStorage)
import { checkAuthStatus } from "../../../utils/authUtils";
import { notify } from "../../../utils/notifications";
import ConfirmDialog from "./ConfirmDialog";
import { UserAuditLog } from "./components/UserAuditLog";
import * as yup from "yup";
import axios from "axios";

interface UserEditModalProps {
  id: string | null;
  onClose: () => void;
  onSaved: () => void;
  onUserDeleted?: (userId: string) => void;
}

// Validation schema for user edit form
const userEditValidationSchema = yup.object({
  firstName: yup
    .string()
    .required("First name is required")
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must be less than 50 characters"),
  lastName: yup
    .string()
    .required("Last name is required")
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must be less than 50 characters"),
  email: yup
    .string()
    .required("Email is required")
    .email("Please enter a valid email address")
    .max(100, "Email must be less than 100 characters"),
  isAdmin: yup.boolean(),
  status: yup
    .string()
    .oneOf(["active", "suspended", "pending"], "Invalid status")
    .required("Status is required"),
});

export default function UserEditModal({ id, onClose, onSaved, onUserDeleted }: UserEditModalProps) {
  const country = useUserCountry();
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // [2025-01-13] user-delete: state for delete confirmation dialog
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // [2025-01-13] user-delete: state for delete operation loading
  const [isDeleting, setIsDeleting] = useState(false);
  // [2025-01-13] status-change: track original status to detect changes
  const [originalStatus, setOriginalStatus] = useState<UserStatus>("active");
  // [2025-01-13] status-change: state for status change confirmation dialog
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  // [2025-01-13] status-change: store pending status change value
  const [pendingStatusChange, setPendingStatusChange] = useState<UserStatus | null>(null);
  // [2025-01-14] Admin password reset: state for reset password confirmation dialog
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  // [2025-01-14] Admin password reset: state for reset password operation loading
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  // [2025-01-15] Audit logging: state for active tab (edit or audit log)
  const [activeTab, setActiveTab] = useState<"edit" | "audit">("edit");
  const [formData, setFormData] = useState<AdminUserUpdateData>({
    firstName: "",
    lastName: "",
    email: "",
    isAdmin: false,
    status: "active",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Fetch user data
  const fetchUser = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await getUser(id);
      const userData = response.data;
      setUser(userData);
      // [2025-01-13] status-change: set original status when user data is loaded
      setOriginalStatus((userData.status || "active") as UserStatus);
      setFormData({
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        isAdmin: userData.isAdmin,
        status: userData.status,
      });
    } catch (err: any) {
      console.error("Error fetching user:", err);

      // Ensure error is always a string
      let errorMessage = "Failed to fetch user data";
      if (err.response?.data?.error) {
        // Handle {error: "message"} format from backend
        errorMessage =
          typeof err.response.data.error === "string"
            ? err.response.data.error
            : JSON.stringify(err.response.data.error);
      } else if (err.response?.data?.message) {
        errorMessage =
          typeof err.response.data.message === "string"
            ? err.response.data.message
            : JSON.stringify(err.response.data.message);
      } else if (err.message) {
        errorMessage = typeof err.message === "string" ? err.message : JSON.stringify(err.message);
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  // Fetch user data when modal opens
  useEffect(() => {
    if (id) {
      fetchUser();
    }
  }, [id, fetchUser]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<any>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Validate form
  const validateForm = async (): Promise<boolean> => {
    try {
      await userEditValidationSchema.validate(formData, { abortEarly: false });
      setFormErrors({});
      return true;
    } catch (err: any) {
      const errors: Record<string, string> = {};
      if (err.inner) {
        err.inner.forEach((error: any) => {
          errors[error.path] = error.message;
        });
      }
      setFormErrors(errors);
      return false;
    }
  };

  // [2025-01-13] status-change: determine if status change needs confirmation
  const shouldConfirmStatusChange = (
    newStatus: UserStatus | undefined,
    oldStatus: UserStatus,
  ): boolean => {
    // [2025-01-13] status-change: return false if newStatus is undefined
    if (!newStatus) return false;

    // Confirm when changing TO Suspended (most restrictive)
    if (newStatus === "suspended") return true;

    // Optional: Confirm when changing FROM Active to Pending
    if (oldStatus === "active" && newStatus === "pending") return true;

    return false;
  };

  // [2025-01-13] status-change: actual save function that performs the update
  const saveUserChanges = async (values: AdminUserUpdateData) => {
    if (!id) return;

    setIsSaving(true);
    setError(null);

    try {
      await updateUser(id, values);

      // [2025-01-13] status-change: update original status after successful save
      if (values.status) {
        setOriginalStatus(values.status);
      }

      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.success("User updated successfully");
      onSaved();
      // [2025-01-13] status-change: close modal after successful save
      onClose();
    } catch (err: any) {
      console.error("Error updating user:", err);

      // Ensure error is always a string
      let errorMessage = "Failed to update user";
      if (err.response?.data?.error) {
        // Handle {error: "message"} format from backend
        errorMessage =
          typeof err.response.data.error === "string"
            ? err.response.data.error
            : JSON.stringify(err.response.data.error);
      } else if (err.response?.data?.message) {
        errorMessage =
          typeof err.response.data.message === "string"
            ? err.response.data.message
            : JSON.stringify(err.response.data.message);
      } else if (err.message) {
        errorMessage = typeof err.message === "string" ? err.message : JSON.stringify(err.message);
      }

      // [2025-01-13] status-change: handle specific error cases for status changes
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 403) {
          // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
          notify.error("You don't have permission to change user status");
        } else if (err.response?.status === 400) {
          // [2025-01-13] status-change: check multiple error response formats
          const responseData = err.response.data;
          let message = responseData.message || responseData.error;

          // Check if error is in errors array format
          if (
            !message &&
            responseData.errors &&
            Array.isArray(responseData.errors) &&
            responseData.errors.length > 0
          ) {
            message = responseData.errors[0].message || responseData.errors[0].error;
          }

          if (
            typeof message === "string" &&
            (message.toLowerCase().includes("cannot suspend yourself") ||
              message.toLowerCase().includes("cannot suspend your own account"))
          ) {
            // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
            notify.error("You cannot suspend your own account");
          } else if (message) {
            // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
            notify.error(typeof message === "string" ? message : "We couldn't change the user status");
          } else {
            // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
            notify.error("We couldn't change the user status");
          }
        } else {
          // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
          notify.error("We couldn't update the user status. Please try again");
        }
      } else {
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error("Something went wrong. Please try again");
      }

      setError(errorMessage);
      throw err; // Re-throw to allow caller to handle
    } finally {
      setIsSaving(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id || !user) return;

    const isValid = await validateForm();
    if (!isValid) return;

    // [2025-01-13] status-change: prevent self-suspension
    const currentUser = await checkAuthStatus();
    if (formData.status === "suspended" && currentUser && currentUser._id === user._id) {
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error("You cannot suspend your own account");
      return;
    }

    // [2025-01-13] status-change: check if status changed and needs confirmation
    const statusChanged = formData.status !== originalStatus;

    if (
      statusChanged &&
      formData.status &&
      shouldConfirmStatusChange(formData.status, originalStatus)
    ) {
      // [2025-01-13] status-change: store pending changes and show confirmation
      setPendingStatusChange(formData.status);
      setShowStatusConfirm(true);
      return; // Don't save yet
    }

    // [2025-01-13] status-change: no status change or already confirmed - proceed with save
    await saveUserChanges(formData);
  };

  // Format date for display
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "Never";
    return formatDateTime(dateString, country);
  };

  // [2025-01-13] status-change: get status change confirmation message
  const getStatusChangeMessage = (
    newStatus: string,
    oldStatus: string,
    userName: string,
  ): string => {
    switch (newStatus) {
      case "suspended":
        return `Are you sure you want to suspend ${userName}?

This will:
• Immediately log them out
• Prevent them from logging in
• Hide their inventory from search results
• Disable their broadcasts

Their account can be reactivated by changing status back to Active.`;

      case "pending":
        return `Are you sure you want to set ${userName}'s status to Pending?

This will:
• Require email verification to login
• Limit their account access
• Mark their account as unverified

Change to Active to restore full access.`;

      case "active":
        if (oldStatus === "suspended") {
          return `Reactivate ${userName}'s account?

This will:
• Allow them to login again
• Show their inventory in search results
• Enable their broadcasts

They will receive an email notification.`;
        }
        return `Set ${userName}'s account to Active?`;

      default:
        return `Change ${userName}'s status from ${oldStatus} to ${newStatus}?`;
    }
  };

  // [2025-01-13] status-change: get status change confirmation title
  const getStatusChangeTitle = (newStatus: UserStatus): string => {
    switch (newStatus) {
      case "suspended":
        return "Suspend User?";
      case "pending":
        return "Set to Pending?";
      case "active":
        return "Activate User?";
      default:
        return "Change Status?";
    }
  };

  // [2025-01-13] status-change: handle status change confirmation
  const handleStatusChangeConfirm = async () => {
    if (!pendingStatusChange) return;

    try {
      // [2025-01-13] status-change: save with confirmed status
      await saveUserChanges({
        ...formData,
        status: pendingStatusChange as UserStatus,
      });

      // [2025-01-13] status-change: close confirmation dialog on success
      setShowStatusConfirm(false);
      setPendingStatusChange(null);
    } catch (err) {
      // [2025-01-13] status-change: keep dialog open on error so user can retry or cancel
      // Error is already handled in saveUserChanges
    }
  };

  // [2025-01-13] status-change: handle status change cancellation
  const handleStatusChangeCancel = () => {
    setShowStatusConfirm(false);
    setPendingStatusChange(null);
  };

  // [2025-01-13] user-delete: handle delete user action
  const handleDelete = async () => {
    if (!id || !user) return;

    // [2025-01-13] user-delete: prevent self-deletion
    const currentUser = await checkAuthStatus();
    if (currentUser && currentUser._id === user._id) {
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error("You cannot delete your own account");
      setShowDeleteConfirm(false);
      return;
    }

    setIsDeleting(true);
    try {
      await deleteUser(user._id);

      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.success("User deleted successfully");

      // [2025-01-13] user-delete: close confirmation dialog
      setShowDeleteConfirm(false);

      // [2025-01-13] user-delete: close edit modal
      onClose();

      // [2025-01-13] user-delete: notify parent to refresh list
      if (onUserDeleted) {
        onUserDeleted(user._id);
      }
    } catch (error: any) {
      console.error("Error deleting user:", error);

      // [2025-01-13] user-delete: handle specific error cases
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 403) {
          // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
          notify.error("You don't have permission to delete users");
        } else if (error.response?.status === 400) {
          // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
          notify.error(error.response.data.message || "We couldn't delete this user");
        } else {
          // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
          notify.error(error, "We couldn't delete this user. Please try again");
        }
      } else {
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error(error, "Something went wrong. Please try again");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // [2025-01-14] Admin password reset: handle password reset action
  const handleResetPassword = async () => {
    if (!id || !user) return;

    setIsResettingPassword(true);
    try {
      await resetUserPassword(user._id);

      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.success(`Password reset email sent to ${user.email}`);
      setShowResetConfirm(false);
    } catch (error: any) {
      console.error("Error resetting password:", error);

      // [2025-01-14] Admin password reset: handle specific error cases
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
          notify.error("User not found");
        } else if (error.response?.status === 400) {
          const message = error.response.data.message || error.response.data.error;
          if (typeof message === "string" && message.toLowerCase().includes("deleted")) {
            // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
            notify.error("We can't reset the password for a deleted user");
          } else {
            // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
            notify.error(message || "We couldn't send the password reset email");
          }
        } else if (error.response?.status === 429) {
          // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
          notify.error("Too many reset requests. Please try again later");
        } else {
          // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
          notify.error("We couldn't send the password reset email. Please try again");
        }
      } else {
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error("Something went wrong. Please try again");
      }
    } finally {
      setIsResettingPassword(false);
    }
  };

  return (
    <Modal show={!!id} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faUser} className="me-2" />
          Edit User
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="text-muted mt-2">Loading user data...</p>
          </div>
        ) : error ? (
          <Alert variant="danger">
            <Alert.Heading>Error</Alert.Heading>
            <p>{error}</p>
            <Button variant="outline-danger" onClick={fetchUser}>
              Retry
            </Button>
          </Alert>
        ) : user ? (
          <>
            {/* [2025-01-15] Audit logging: Tab navigation */}
            <Nav
              variant="tabs"
              className="mb-4"
              activeKey={activeTab}
              onSelect={(k) => setActiveTab((k as "edit" | "audit") || "edit")}
            >
              <Nav.Item>
                <Nav.Link eventKey="edit">
                  <FontAwesomeIcon icon={faUser} className="me-2" />
                  Edit User
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="audit">
                  <FontAwesomeIcon icon={faHistory} className="me-2" />
                  Activity History
                </Nav.Link>
              </Nav.Item>
            </Nav>

            {/* [2025-01-15] Audit logging: Tab content */}
            {activeTab === "edit" ? (
              <Form onSubmit={handleSubmit}>
                {/* User Information */}
                <Row className="mb-4">
                  <Col>
                    <h6 className="text-muted mb-3">User Information</h6>
                  </Col>
                </Row>

                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        <FontAwesomeIcon icon={faUser} className="me-1" />
                        First Name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        isInvalid={!!formErrors.firstName}
                        placeholder="Enter first name"
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.firstName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        <FontAwesomeIcon icon={faUser} className="me-1" />
                        Last Name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        isInvalid={!!formErrors.lastName}
                        placeholder="Enter last name"
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.lastName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-3 mt-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                        Email
                      </Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        isInvalid={!!formErrors.email}
                        placeholder="Enter email address"
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.email}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                        Role
                      </Form.Label>
                      <Form.Select
                        name="isAdmin"
                        value={formData.isAdmin ? "admin" : "user"}
                        onChange={(e) =>
                          handleInputChange({
                            target: {
                              name: "isAdmin",
                              value: e.target.value === "admin",
                              type: "checkbox",
                              checked: e.target.value === "admin",
                            } as any,
                          } as any)
                        }
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-3 mt-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        <FontAwesomeIcon icon={faToggleOn} className="me-1" />
                        Status
                      </Form.Label>
                      <Form.Select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        isInvalid={!!formErrors.status}
                      >
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                        <option value="pending">Pending</option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {formErrors.status}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Email Verified</Form.Label>
                      <div className="mt-2">
                        <Badge bg={user.email_verified === "Y" ? "success" : "secondary"}>
                          {user.email_verified === "Y" ? "Verified" : "Unverified"}
                        </Badge>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {/* User Metadata */}
                <Row className="mt-4">
                  <Col>
                    <h6 className="text-muted mb-3">Account Information</h6>
                    <div className="bg-light p-3 rounded">
                      <Row className="g-2">
                        <Col md={6}>
                          <small className="text-muted">Created:</small>
                          <div>{formatDate(user.createdAt)}</div>
                        </Col>
                        <Col md={6}>
                          <small className="text-muted">Last Updated:</small>
                          <div>{formatDate(user.updatedAt)}</div>
                        </Col>
                        <Col md={6}>
                          <small className="text-muted">Last Login:</small>
                          <div>{formatDate(user.lastLogin)}</div>
                        </Col>
                        <Col md={6}>
                          <small className="text-muted">Login Count:</small>
                          <div>{user.loginCount}</div>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Form>
            ) : (
              <UserAuditLog
                userId={user._id}
                userName={`${user.firstName} ${user.lastName}`.trim() || user.email}
              />
            )}
          </>
        ) : null}
      </Modal.Body>

      <Modal.Footer>
        {/* [2025-01-14] Button layout: restructured footer with proper alignment */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            gap: "var(--spacing-md, 16px)",
          }}
        >
          {/* Left group: Action buttons */}
          <div style={{ display: "flex", gap: "var(--spacing-sm, 8px)" }}>
            {user && (
              <>
                {/* [2025-01-13] user-delete: delete button */}
                {/* [2025-01-14] Button standardization: shortened label for consistent sizing */}
                {/* [2025-01-14] Button standardization: using design system CSS variables for colors */}
                <Button
                  variant="outline-danger"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isSaving || isDeleting || isResettingPassword}
                  style={{
                    color: "var(--color-status-red)",
                    borderColor: "var(--color-status-red)",
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} className="me-2" />
                  Delete
                </Button>
                {/* [2025-01-14] Admin password reset: reset password button */}
                {/* [2025-01-14] Button standardization: shortened label for consistent sizing */}
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowResetConfirm(true)}
                  disabled={isSaving || isDeleting || isResettingPassword || !!user.deletedAt}
                  title="Send password reset email to user"
                >
                  <FontAwesomeIcon icon={faKey} className="me-2" />
                  Reset
                </Button>
              </>
            )}
          </div>

          {/* Right group: Standard buttons */}
          <div style={{ display: "flex", gap: "var(--spacing-sm, 8px)" }}>
            <Button
              variant="outline-secondary"
              onClick={onClose}
              disabled={isSaving || isDeleting || isResettingPassword}
            >
              Cancel
            </Button>
            {user && (
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isSaving || isDeleting || isResettingPassword}
              >
                {isSaving ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            )}
          </div>
        </div>
      </Modal.Footer>

      {/* [2025-01-13] user-delete: confirmation dialog for delete action */}
      {user && (
        <ConfirmDialog
          show={showDeleteConfirm}
          title="Delete User?"
          message={`Are you sure you want to delete ${user.firstName} ${user.lastName}? This will:

• Set their account status to "Deleted"
• Hide their inventory from search
• Prevent them from logging in
• Keep their data for audit purposes

This action can be reversed by reactivating the account.`}
          confirmText="Delete User"
          cancelText="Cancel"
          variant="danger"
          isLoading={isDeleting}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          size="lg"
        />
      )}

      {/* [2025-01-13] status-change: confirmation dialog for status changes */}
      {user && showStatusConfirm && pendingStatusChange && (
        <ConfirmDialog
          show={showStatusConfirm}
          title={getStatusChangeTitle(pendingStatusChange)}
          message={getStatusChangeMessage(
            pendingStatusChange,
            originalStatus,
            `${user.firstName} ${user.lastName}`,
          )}
          confirmText={pendingStatusChange === "suspended" ? "Suspend User" : "Change Status"}
          cancelText="Cancel"
          variant={pendingStatusChange === "suspended" ? "danger" : "warning"}
          isLoading={isSaving}
          onConfirm={handleStatusChangeConfirm}
          onCancel={handleStatusChangeCancel}
          size="lg"
        />
      )}

      {/* [2025-01-14] Admin password reset: confirmation dialog for password reset */}
      {user && (
        <ConfirmDialog
          show={showResetConfirm}
          title="Send Password Reset Email?"
          message={`Send a password reset email to ${user.firstName} ${user.lastName}?

The email will be sent to: ${user.email}

They will receive a link to create a new password. The link will expire in 1 hour.`}
          confirmText="Send Reset Email"
          cancelText="Cancel"
          confirmVariant="info"
          isLoading={isResettingPassword}
          onConfirm={handleResetPassword}
          onCancel={() => setShowResetConfirm(false)}
          size="lg"
        />
      )}
    </Modal>
  );
}
// [2026-07-08] redeploy: re-commit cookie-auth fix to trigger rebuild
